import type { z } from 'zod'
import { ApiError } from './api-error'
import { buildUrl, type Endpoint } from './endpoint'
import { reportContractMismatch } from './report-contract-mismatch'
import { TransportError, type Transport, type TransportRequest, type TransportResponse } from './transport'

export interface RequestOptions {
  /**
   * Overrides the bound school for this call (the super-admin notification
   * bell). `null` sends no `x-school-id`; `undefined` keeps the bound school.
   * Key the query on the same value.
   */
  schoolId?: string | null
  /** Default 60 s. Raise it only for an endpoint whose work is genuinely long and synchronous. */
  timeoutMs?: number
  /** Pass TanStack Query's `signal` so a cancelled query aborts its request. */
  signal?: AbortSignal
  /** Passed to `fetch` (Next caching on the server). */
  cache?: RequestCache
  /** Passed to `fetch` (Next caching on the server). */
  next?: { revalidate?: number | false; tags?: string[] }
}

export interface BackendClient {
  /** The school this client is bound to and sends as `x-school-id`. Use it in query keys. */
  readonly schoolId: string | null
  /** Sends the request and parses a 2xx body with `schema`. Every failure throws `ApiError`. */
  request<T>(endpoint: Endpoint, schema: z.ZodType<T>, opts?: RequestOptions): Promise<T>
  /** Sends the request and hands back status, headers and raw body (blobs, 204s). A non-2xx still throws `ApiError`. */
  send(endpoint: Endpoint, opts?: RequestOptions): Promise<TransportResponse>
}

/**
 * What a schema mismatch on a 2xx response does. `throw` raises
 * `ApiError{ kind: 'contract' }`. `report` logs it (see
 * `reportContractMismatch`) and returns the raw payload, the behaviour of
 * `parseResponse` today: a screen with a wrong date beats a blank screen.
 */
export type ContractMismatchPolicy = 'throw' | 'report'

export interface BackendClientConfig {
  transport: Transport
  getToken: () => Promise<string | null>
  /** Sent as `x-school-id` on every request unless `opts.schoolId` overrides it. Never re-read. */
  schoolId: string | null
  /**
   * Defaults to `BACKEND_INTERNAL_URL` on the server when set (a preview box's
   * loopback API address), else `NEXT_PUBLIC_BACKEND_URL`. A browser always
   * uses `NEXT_PUBLIC_BACKEND_URL`.
   */
  baseUrl?: string
  /** Defaults to `throw` under Vitest and `report` everywhere else. */
  contractMismatch?: ContractMismatchPolicy
}

/** The wait every call gets unless it asks for more. */
const DEFAULT_TIMEOUT_MS = 60_000

/** The legacy transport's message per status, used when the backend sent none. */
const FALLBACK_MESSAGES: Record<number, string> = {
  400: 'Bad request.',
  401: 'Session expired. Please log in again.',
  500: 'Server error.'
}

const NETWORK_MESSAGE = 'Network error - no response received from server.'

const defaultPolicy = (): ContractMismatchPolicy => (process.env.NODE_ENV === 'test' ? 'throw' : 'report')

function isRawBody(body: unknown): body is FormData | Blob {
  return (
    (typeof FormData !== 'undefined' && body instanceof FormData) ||
    (typeof Blob !== 'undefined' && body instanceof Blob)
  )
}

/** `x-demo-mode` comes from localStorage, so only a browser sends it. */
function isDemoMode(): boolean {
  return typeof window !== 'undefined' && window.localStorage.getItem('demo-mode') === 'true'
}

/** JSON-serialises a body, except `FormData` and `Blob`, which go out as-is with no `Content-Type`. */
function encodeBody(body: unknown): { body?: BodyInit; contentType?: string } {
  if (body === undefined || body === null) return {}
  if (isRawBody(body)) return { body }
  return { body: JSON.stringify(body), contentType: 'application/json' }
}

function buildHeaders(schoolId: string | null, contentType: string | undefined): Record<string, string> {
  const headers: Record<string, string> = {}
  if (schoolId) headers['x-school-id'] = schoolId
  if (isDemoMode()) headers['x-demo-mode'] = 'true'
  if (contentType) headers['Content-Type'] = contentType
  return headers
}

/** The bearer token, or an `auth` ApiError when there is none or fetching it failed. */
async function requireToken(getToken: () => Promise<string | null>, label: string): Promise<string> {
  let token: string | null
  try {
    token = await getToken()
  } catch (error) {
    throw new ApiError('No authentication token available', 401, { kind: 'auth', endpoint: label, cause: error })
  }
  if (!token) throw new ApiError('No authentication token available', 401, { kind: 'auth', endpoint: label })
  return token
}

/**
 * Reads `{ error: { type, message, details } }` leniently: the backend's real
 * error shape, which `ErrorResponseSchema` in shared-types does not describe.
 * Also accepts `{ error: '...' }`, and a body with no `error` key (the older
 * bare `{ message }`, or a domain shape), which it keeps whole as `details`.
 */
function readErrorBody(body: unknown): { type?: string; message?: string; details?: unknown } {
  if (!body || typeof body !== 'object' || body instanceof Blob) {
    return typeof body === 'string' && body ? { message: body.slice(0, 200) } : {}
  }
  const record = body as Record<string, unknown>
  const nested = record.error
  if (nested && typeof nested === 'object') {
    const err = nested as Record<string, unknown>
    return {
      type: typeof err.type === 'string' ? err.type : undefined,
      message: typeof err.message === 'string' ? err.message : undefined,
      details: err.details
    }
  }
  if (typeof nested === 'string') return { message: nested }
  // No envelope: a domain-specific body (the Arbiter `{ kind, message }` 502).
  // It is kept whole as `details` so the domain catch can read it.
  return { message: typeof record.message === 'string' ? record.message : undefined, details: record }
}

function httpError(res: TransportResponse, req: TransportRequest, label: string): ApiError {
  const { type, message, details } = readErrorBody(res.body)

  // A 428 is the backend asking for school setup: expected, so informational.
  if (res.status === 428) {
    console.info(`ℹ️ Backend ${req.method} ${req.url} - School setup required:`, message || 'Missing critical data')
  } else {
    console.error(`❌ Backend ${req.method} ${req.url} failed:`, `Status ${res.status} - ${message || 'No details'}`)
  }

  return new ApiError(message || FALLBACK_MESSAGES[res.status] || 'Unknown error occurred.', res.status, {
    type,
    details,
    endpoint: label
  })
}

function transportFailure(error: unknown, req: TransportRequest, label: string): unknown {
  // The caller aborted (a cancelled query): let its abort reason through untouched.
  if (req.signal?.aborted) return error

  const timedOut = error instanceof TransportError && error.kind === 'timeout'
  console.error(`❌ Backend ${req.method} ${req.url} failed:`, error instanceof Error ? error.message : error)
  return new ApiError(timedOut ? `Request timed out after ${req.timeoutMs / 1000}s.` : NETWORK_MESSAGE, 0, {
    kind: timedOut ? 'timeout' : 'network',
    endpoint: label,
    cause: error
  })
}

/**
 * The server prefers the server-only internal URL so server-rendered requests
 * reach the API over loopback on a preview box. A browser never sees it.
 */
function defaultBaseUrl(): string | undefined {
  if (typeof window === 'undefined' && process.env.BACKEND_INTERNAL_URL) return process.env.BACKEND_INTERNAL_URL
  return process.env.NEXT_PUBLIC_BACKEND_URL
}

/**
 * Builds a backend client bound to one school and one token source.
 *
 * The school is fixed here, and a request reads it BEFORE awaiting the token.
 * The legacy transport read the active-school cookie after that await, so a
 * school switch inside the window sent school B on a request cached under
 * school A. A new school means a new client.
 */
export function createBackendClient(cfg: BackendClientConfig): BackendClient {
  const { transport, getToken, schoolId: boundSchoolId } = cfg
  const baseUrl = cfg.baseUrl ?? defaultBaseUrl()
  const policy = cfg.contractMismatch ?? defaultPolicy()

  async function send(endpoint: Endpoint, opts: RequestOptions = {}): Promise<TransportResponse> {
    if (!baseUrl) throw new Error('NEXT_PUBLIC_BACKEND_URL is not set')
    const url = buildUrl(baseUrl, endpoint)
    const label = `${endpoint.method} ${endpoint.path}`
    const schoolId = opts.schoolId !== undefined ? opts.schoolId : boundSchoolId

    const { body, contentType } = encodeBody(endpoint.body)
    const headers = buildHeaders(schoolId, contentType)
    if (endpoint.auth !== 'none') headers.Authorization = `Bearer ${await requireToken(getToken, label)}`

    const req: TransportRequest = {
      method: endpoint.method,
      url,
      headers,
      body,
      timeoutMs: opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
      signal: opts.signal,
      cache: opts.cache,
      next: opts.next
    }

    let res: TransportResponse
    try {
      res = await transport(req)
    } catch (error) {
      throw transportFailure(error, req, label)
    }

    if (res.status < 200 || res.status >= 300) throw httpError(res, req, label)
    return res
  }

  async function request<T>(endpoint: Endpoint, schema: z.ZodType<T>, opts?: RequestOptions): Promise<T> {
    const res = await send(endpoint, opts)
    const parsed = schema.safeParse(res.body)
    if (parsed.success) return parsed.data

    const label = `${endpoint.method} ${endpoint.path}`
    if (policy === 'throw') {
      throw new ApiError(`Response from ${label} did not match its schema`, res.status, {
        kind: 'contract',
        details: parsed.error.issues.slice(0, 5),
        endpoint: label
      })
    }
    reportContractMismatch(label, parsed.error.issues)
    return res.body as T
  }

  return { schoolId: boundSchoolId, request, send }
}
